import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Col, Divider, Form, Row, Select } from 'antd';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../Commons';
import { StepContext, WorkflowContext } from '../../Commons/Context';
import { api } from '../../Http';
import { delegateSaleForce } from '../../Http/salesforce/delegate';
import './style.scss';

const INPUTS = [
  { name: 'creditNoteField', path: 'CreditNoteID', label: 'Xero Credit Note ID', types: ['string', 'id', 'reference'] },
  { name: 'invoiceField', path: 'Allocations[0].Invoice.InvoiceID', label: 'Xero Invoice ID', types: ['string', 'id', 'reference'] },
  { name: 'amountField', path: 'Allocations[0].Amount', label: 'Allocation amount', types: ['currency', 'double', 'int'] },
  { name: 'dateField', path: 'Allocations[0].Date', label: 'Allocation date', types: ['date'] },
];
const OUTPUTS = [
  { name: 'returnField', type: 'ACT', path: 'AllocationID', label: 'Save allocation ID to', required: true },
  { name: 'logField', type: 'LOG', path: 'AllocationResponse', label: 'Full log', description: 'Optional. Saves the complete response or error details for troubleshooting, up to 131,072 characters. Use a long text field.' },
  { name: 'statusField', type: 'LOGS', path: 'AllocationStatus', label: 'Result status', description: 'Optional. Saves SUCCESS or ERROR so you can see the outcome at a glance.' },
  { name: 'messageField', type: 'LOGM', path: 'AllocationMessage', label: 'Result message', description: 'Optional. Saves the specific Xero validation error, or the response message or status on success.' },
];

const CreditNoteAllocationForm = () => {
  const { id, accountAuthorized } = useContext(StepContext);
  const { SObject, sampleData, mappings, setMappings, workflowId } = useContext(WorkflowContext);
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [metadataError, setMetadataError] = useState('');
  const [retry, setRetry] = useState(0);
  const [saved, setSaved] = useState(false);
  const history = useHistory();
  const objectUrl = SObject?.urls?.describe;
  const ready = Boolean(SObject?.name && sampleData?.Id && accountAuthorized);

  useEffect(() => {
    const current = (mappings || []).filter(mapping => String(mapping.stepId) === String(id));
    const values = {};
    INPUTS.forEach(input => { values[input.name] = current.find(m => m.type === 'TRIG' && m.field === input.path)?.value; });
    OUTPUTS.forEach(output => { const m = current.find(m => m.type === output.type);
      values[output.name] = output.type === 'ACT' ? m?.field : m?.value; });
    form.setFieldsValue(values);
  }, [form, id, mappings]);

  useEffect(() => {
    let active = true;
    setFields([]);
    if (!objectUrl) return;
    setLoading(true);
    setMetadataError('');
    delegateSaleForce({ uri: objectUrl }).then(response => {
      if (response.code !== 0 || !Array.isArray(response.data?.fields)) {
        throw new Error(response.message || 'Could not load Salesforce fields.');
      }
      if (active) setFields(response.data.fields);
    }).catch(error => {
      if (active) setMetadataError(error.message || 'Could not load Salesforce fields.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [objectUrl, retry]);

  const sourceFields = fields.filter(field =>
    Object.prototype.hasOwnProperty.call(sampleData || {}, field.name) &&
    field.type !== 'address' && field.type !== 'location');
  const destinationFields = fields.filter(field => field.updateable &&
    ['string', 'textarea'].includes(field.type));

  const save = async (values) => {
    if (!ready || saving || saved) return;
    setSaving(true);
    setError('');
    const nextMappings = INPUTS.map(input => ({ stepId: id, type: 'TRIG', field: input.path, value: values[input.name], valueStepOrder: 1 }));
    OUTPUTS.filter(output => values[output.name]).forEach(output => nextMappings.push({
      stepId: id, type: output.type, field: output.type === 'ACT' ? values[output.name] : output.path,
      value: output.type === 'ACT' ? output.path : values[output.name], valueStepOrder: 1,
    }));
    try {
      const response = await api.post('/api/v1/workflows/mappings', {
        mappings: nextMappings, sObject: SObject.name, workflowId,
      });
      if (response.data.code !== 0) throw new Error(response.data.message || 'Could not save the workflow.');
      setMappings(nextMappings);
      setSaved(true);
    } catch (error) {
      setError(error.data?.message || error.message || 'Could not save the workflow. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row className='padded xinvform'>
      <div className='title'>Allocate a Credit Note</div>
      <Col span={24}>
        <p>Allocate an existing Xero credit note to an invoice using values from a new Salesforce record.</p>
        <p className='hint'>Choose Salesforce fields containing the Xero IDs, amount, and date. Uses your connected Xero organisation. Saving configures the workflow; allocations are created when the webhook runs.</p>
        {!ready && <Alert type='info' showIcon message='Choose your accounts, select a Salesforce object, and test the Salesforce trigger before mapping fields.' />}
        {ready && !objectUrl && <Alert type='warning' showIcon message='Select the Salesforce object again to load its field details.' />}
        {metadataError && <Alert type='error' showIcon message={metadataError}
          action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>} />}
        {error && <Alert type='error' showIcon message={error} />}
        {saved ? <>
          <Alert type='success' showIcon message='Workflow mappings saved.'
            description='The Salesforce webhook will create the allocation in Xero and write back the returned allocation ID.' />
          <PrimaryButton className='btn-long' size='large' onClick={() => history.push('/system/workflows')}>Done</PrimaryButton>
        </> : <Form form={form} layout='vertical' onFinish={save} scrollToFirstError
          disabled={!ready || loading || saving || Boolean(metadataError)}>
          <Row justify='center'>
            <Col span={22}>
              <section aria-labelledby='allocation-mapping-heading'>
                <h3 id='allocation-mapping-heading'>Mapping to Xero</h3>
                <p>Select the Salesforce fields used to create the credit note allocation.</p>
              {INPUTS.map(input => <Form.Item key={input.name} name={input.name} label={input.label} rules={[
                { required: true, message: 'Select a Salesforce source field.' },
                { validator: (_, value) => !value || sourceFields.some(field => field.name === value && input.types.includes(field.type))
                  ? Promise.resolve() : Promise.reject(new Error('Select an available field of the correct type.')) },
              ]}>
                <Select showSearch allowClear optionFilterProp='label' placeholder='Select a Salesforce field'
                  options={sourceFields.filter(field => input.types.includes(field.type)).map(field => ({ value: field.name, label: field.label + ' (' + field.name + '): ' + (sampleData?.[field.name] ?? '(empty)') }))} />
              </Form.Item>)}
              </section>
              <Divider style={{ margin: '32px 0' }} />
              <section aria-labelledby='allocation-reply-heading'>
                <h3 id='allocation-reply-heading'>Reply back to Salesforce</h3>
                <p>Choose where to save the allocation ID and optional results on the Salesforce record.</p>
              {OUTPUTS.map(output => <Form.Item key={output.name} name={output.name} label={output.label}
                dependencies={[...INPUTS.map(input => input.name), ...OUTPUTS.filter(other => other.name !== output.name).map(other => other.name)]}
                rules={[
                  { required: output.required, message: 'Select a writable Salesforce text field.' },
                  { validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const used = [...INPUTS, ...OUTPUTS].filter(other => other.name !== output.name).some(other => form.getFieldValue(other.name) === value);
                    return destinationFields.some(field => field.name === value) && !used
                      ? Promise.resolve() : Promise.reject(new Error('Use a separate writable text field.'));
                  } },
                ]} extra={output.required ? 'Stores the allocation ID returned by Xero.' : output.description}>
                <Select showSearch allowClear optionFilterProp='label' placeholder='Select a Salesforce destination field'
                  options={destinationFields.map(field => ({ value: field.name, label: field.label + ' (' + field.name + ')' }))} />
              </Form.Item>)}
              </section>
            </Col>
          </Row>
          <PrimaryButton className='btn-long' size='large' htmlType='submit' loading={saving}
            disabled={!ready || loading || !fields.length || Boolean(metadataError)}>Save Workflow</PrimaryButton>
        </Form>}
      </Col>
    </Row>
  );
};

export default CreditNoteAllocationForm;
